import { assert } from '@ember/debug';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy';
import { throttle, later, scheduleOnce, cancel } from '@ember/runloop';
import { type EmberRunTimer } from '@ember/runloop/types';
import { htmlSafe } from '@ember/template';
import { type SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import { resolve } from 'rsvp';
import { localCopy } from 'tracked-toolbox';

// eslint-disable-next-line no-undef
const { escapeExpression } = (<typeof Handlebars>(<unknown>Ember.Handlebars)).Utils;

interface VirtualEachSignature<T> {
  Args: {
    items: T[];
    height?: number;
    rowPadding?: number;
    itemHeight?: number;
    positionIndex: number;
    scrollTimeout?: number;
    onScrollBottomed?: (startAt: number, endAt: number) => unknown;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: [item: T, actualIndex: number, virtualIndex: number];
    inverse: [];
  };
}

interface VirtualEachItem<T> {
  raw: T;
  actualIndex: number;
  virtualIndex: number;
}

export default class VirtualEach<T> extends Component<VirtualEachSignature<T>> {
  isWebkit = /WebKit/.test(navigator && navigator.userAgent);
  declare element: HTMLDivElement;

  @localCopy('args.items') items!: VirtualEachSignature<T>['Args']['items'];
  @localCopy('args.height', 200) height!: NonNullable<VirtualEachSignature<T>['Args']['height']>;
  @localCopy('args.itemHeight', 20) itemHeight!: NonNullable<VirtualEachSignature<T>['Args']['itemHeight']>;
  @localCopy('args.scrollTimeout', 30) scrollTimeout!: VirtualEachSignature<T>['Args']['scrollTimeout'];
  @localCopy('args.rowPadding', 1) bufferSize!: NonNullable<VirtualEachSignature<T>['Args']['rowPadding']>;
  @localCopy('args.positionIndex') positionIndex!: VirtualEachSignature<T>['Args']['positionIndex'];

  @tracked scrolledByWheel = false;
  @tracked startAt = 0;
  @tracked totalHeight = 0;
  @tracked scrollThrottleTimeout?: EmberRunTimer;
  @tracked scrollBottomedTimeout?: EmberRunTimer;
  @tracked scheduledRender?: EmberRunTimer;

  @action
  onInsert(element: HTMLDivElement): void {
    this.element = element;

    // A splatted `style` replaces this component's own, silently dropping the height; the root then
    // never scrolls and only the first viewport of rows is ever reachable.
    assert(
      `<VirtualEach> needs height:${this.height}px on its root — include it in any style you pass.`,
      element.style.height === `${this.height}px`,
    );

    this.#updateTotalHeight();
  }

  get style(): SafeString {
    return htmlSafe(`height: ${this.height}px;`);
  }

  get contentStyle(): SafeString {
    return htmlSafe(`height: ${escapeExpression(this.contentHeight.toString())}px; margin-top: ${this.marginTop}px;`);
  }

  get visibleItems(): VirtualEachItem<T>[] {
    const { items, startAt, itemCount, bufferSize } = this;
    const itemsLength = items.length;
    const endAt = Math.min(itemsLength, startAt + itemCount);
    const { onScrollBottomed } = this.args;

    if (typeof onScrollBottomed === 'function' && startAt + itemCount - bufferSize >= itemsLength) {
      // eslint-disable-next-line ember/no-side-effects, ember/no-runloop
      this.scrollBottomedTimeout = later(() => onScrollBottomed(startAt, endAt), 5);
    }

    return items.slice(startAt, endAt).map((item, index) => {
      return {
        raw: item,
        actualIndex: startAt + index,
        virtualIndex: index,
      };
    });
  }

  get itemCount(): number {
    const { bufferSize, height, itemHeight } = this;
    return Math.ceil(height / itemHeight) + bufferSize;
  }

  get marginTop(): number {
    const { bufferSize, itemHeight, totalHeight, startAt, itemCount: visibleItemCount } = this;
    const margin = startAt * itemHeight;
    const maxMargin = Math.max(0, totalHeight - (visibleItemCount - 1) * itemHeight + bufferSize * itemHeight);

    return Math.min(maxMargin, margin);
  }

  get contentHeight(): number {
    return this.totalHeight - this.marginTop;
  }

  @action
  calculateVisibleItems(positionIndex = NaN): void {
    const { startAt, itemHeight } = this;
    const scrolledAmount = this.element.scrollTop;
    const visibleStart = isNaN(positionIndex) ? Math.floor(scrolledAmount / itemHeight) : positionIndex;

    if (visibleStart !== startAt) {
      this.startAt = visibleStart;
    }
  }

  @action
  onUpdate(): void {
    this.scrollTo();
  }

  @action
  onUpdateItems(): void {
    this.#updateTotalHeight();
  }

  @action
  scrollTo(): void {
    const { bufferSize, positionIndex, itemHeight, totalHeight, itemCount, items, startAt } = this;
    const startingIndex = isNaN(positionIndex) ? startAt : Math.max(positionIndex, 0);
    const startingPadding = itemHeight * startingIndex;
    const maxVisibleItemTop = Math.max(0, items.length - itemCount + bufferSize);
    const maxPadding = Math.max(0, totalHeight - (itemCount - 1) * itemHeight + bufferSize * itemHeight);
    const sanitizedIndex = Math.min(startingIndex, maxVisibleItemTop);
    const sanitizedPadding = startingPadding > maxPadding ? maxPadding : startingPadding;

    const afterRenderFn = () => {
      this.calculateVisibleItems(sanitizedIndex);
      this.element.scrollTop = sanitizedPadding;
    };

    // eslint-disable-next-line ember/no-runloop
    this.scheduledRender = scheduleOnce('afterRender', this, afterRenderFn);
  }

  @action
  onDestroy(): void {
    // eslint-disable-next-line ember/no-runloop
    cancel(this.scheduledRender);
    // eslint-disable-next-line ember/no-runloop
    cancel(this.scrollThrottleTimeout);
    // eslint-disable-next-line ember/no-runloop
    cancel(this.scrollBottomedTimeout);
  }

  @action
  onScroll(e: Event): void {
    e.preventDefault();
    const { scrollTimeout, isWebkit, scrolledByWheel } = this;

    if (scrollTimeout && isWebkit && scrolledByWheel) {
      this.scrolledByWheel = false;
      // eslint-disable-next-line ember/no-runloop
      this.scrollThrottleTimeout = throttle(() => {
        this.calculateVisibleItems();
      }, scrollTimeout);
      return;
    }

    this.calculateVisibleItems();
  }

  @action
  onWheel(): void {
    this.scrolledByWheel = true;
  }

  #updateTotalHeight() {
    void resolve(this.items).then((items) => {
      this.totalHeight = Math.max(items.length * this.itemHeight, 0);
    });
  }

  <template>
    <div
      class="overflow-y-auto w-full"
      style={{this.style}}
      ...attributes
      {{didInsert this.onInsert}}
      {{didUpdate this.onUpdate @positionIndex}}
      {{didUpdate this.onUpdateItems @items}}
      {{willDestroy this.onDestroy}}
      {{on "scroll" this.onScroll}}
      {{on "wheel" this.onWheel}}
    >
      <ul class="list-none p-0 m-0 translate-y-0" style={{this.contentStyle}}>
        {{#each this.visibleItems key="raw.id" as |item idx|}}
          <li class={{concat "virtual-item visible-item-" idx}}>
            {{yield item.raw item.actualIndex item.virtualIndex}}
          </li>
        {{else}}
          <li class="virtual-list-empty">
            {{yield to="inverse"}}
          </li>
        {{/each}}
      </ul>
    </div>
  </template>
}
